/**
 * ADDRESS GRAMMAR — 09 §1.5, premise P2.3/P2.4.
 *
 * "A URL never contains a business value." No `?db=b038`, no ח.פ, no Priority code, no FIN cid,
 * no unit slug. Only opaque `<entity>_uid`. The consequence is the point: a bookmarked link keeps
 * working when a company is renamed, re-linked to a different source db, or merged — because the
 * link names the internal company, not the source system's code.
 *
 * Every address in the app is built here. A hand-written template string elsewhere is a defect.
 */

/**
 * Opaque uid shapes THIS PRODUCT ISSUES. Anything that is not one of them must never reach an
 * address.
 *
 * ── THIS WAS ONE REGEX, AND NOTHING IN THE PRODUCT HAS EVER ISSUED THAT SHAPE ────────────────
 *
 * It read `^[a-z]{3}_[A-Za-z0-9_-]{1,64}$`. **Twenty-five of the twenty-seven `*_uid` columns in the migrations are
 * `UNIQUEIDENTIFIER`**, and the two that are not name no address, the stores project them verbatim (`tenantUid: row.tenant_uid`), and the
 * API hands the GUID to the browser unchanged. So every builder below threw on every real row,
 * and the throw reached `RenderFaultBoundary`, which is a BLANK PAGE with the words
 * "אירעה תקלה" and no reference code — truthfully none, because no request was ever made.
 *
 * MEASURED ON STAGING, 2026-10-18, release `782bb4b14603`: `/platform` rendered correctly while
 * the estate held zero tenants, and began answering with that state the moment the first tenant
 * row existed — `platformTenant(...)` throws while rendering the recent-customers table. The
 * timing made it look like a consequence of the failed tenant creation. It is not: it is this
 * guard, which had been wrong since it was written and had no row to prove it on.
 *
 * ── AND WHY NO TEST SAW IT ──────────────────────────────────────────────────────────
 *
 * `api/fixtures.ts` invents uids in the prefixed shape — `tnt_fixture_0001`. The fixture and the
 * guard agreed with each other, and neither agreed with the server. That is the same failure as
 * the invented capability codes, in a different file: **a fixture is not evidence about the
 * estate.** `routes.test.ts` now binds this file to the migrations instead.
 *
 * ── THE PREMISE IS UNCHANGED ────────────────────────────────────────────────────────
 *
 * 09 §1.5 / P2.4 forbids a BUSINESS value in an address: a slug, a ח.פ, a Priority code, a source
 * `db` name. A GUID is the opposite of one — opaque, stable across a rename or a re-link, and
 * meaningless outside this product. Both shapes below are opaque; neither can carry a business
 * fact. The guard still refuses everything else, and `routes.test.ts` proves it on the exact
 * values the premise names.
 */
const PREFIXED_UID = /^[a-z]{3}_[A-Za-z0-9_-]{1,64}$/;

/**
 * `UNIQUEIDENTIFIER`, canonical 8-4-4-4-12.
 *
 * Case-insensitive on purpose rather than by habit: SQL Server's `NEWID()` renders upper-case and
 * `tedious` returns it that way, while `randomUUID()` in `provisioning-store.ts` produces
 * lower-case. BOTH are the same uid, and an address builder that accepted one and threw on the
 * other would fail depending on which row it was handed.
 */
const GUID_UID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Fails loudly rather than putting a ח.פ or a `db` code in the address bar. This is a
 * developer guardrail, not a permission check — the gate decides access, always.
 */
function uid(kind: string, value: string): string {
  if (!PREFIXED_UID.test(value) && !GUID_UID.test(value)) {
    throw new Error(
      `Refusing to build an address from a non-uid ${kind}: "${value}". ` +
        '09 §1.5 / P2.4 — a URL carries only opaque uids, never a business value.',
    );
  }
  return value;
}

export const routes = {
  signIn: '/signin',
  /* The public page. No session, no surface — see `preSessionRoutePatterns` below. */
  welcome: '/welcome',

  /*
   * THE TWO MAILED ADDRESSES. Both carry a one-time token THAT ARRIVED IN SOMEBODY'S INBOX, and it
   * rides as the LAST PATH SEGMENT — never as `?token=`. **X8, AMEND-3 §C-4.**
   *
   * This comment used to say `?token=`, and the screens used to read `params.get('token')`, which is
   * the carrier SEC9 forbids: a query string is what gets copied wholesale into analytics tags, error
   * reports and `Referer` headers, and the value here completes an account takeover. The compensating
   * controls (single use, one conditional UPDATE, short-lived, stored only as SHA-256) are
   * `invitation-token.ts`'s and are unchanged; the carrier is not theirs to excuse.
   *
   * These builders are the BASE addresses — the screen the recipient lands on. The token is appended
   * by the producer, `buildLink` in `@glpoint/mail`, which is why there is no `(token: string)`
   * builder here: a web-side builder would be a second place that decides where a live credential
   * sits in a url. What this file owns is the ROUTER PATTERN below, which must accept the segment.
   *
   * They sit outside `/tenant` and outside `/security` because the person opening them HAS NO
   * SESSION — an invitee has no account yet, and somebody who has forgotten a password cannot be
   * asked to sign in to fix it. An address under a signed-in section would be one the router sends
   * to sign-in, which is the loop G-10 records as a permanent lockout.
   */
  invitationAccept: '/invitation',
  passwordReset: '/signin/reset',
  /*
   * THE THIRD MAILED ADDRESS. **Owner section 1, 2026-10-29.** Same carrier as the other two: the
   * token is the LAST PATH SEGMENT and never `?token=`, and `buildLink` appends it.
   */
  emailChangeConfirm: '/verify-email',
  /*
   * ── 0032 · THE PUBLIC DOOR. TWO ADDRESSES, AND THE SECOND IS A MAILED ONE. ──────────────
   *
   * `/signup` is where a stranger starts. `/signup/verify` is where the mailed link lands, and it
   * is NOT `/verify-email` -- that address belongs to the login-address change, and a registering
   * stranger arriving there would meet a screen about an account they do not have.
   *
   * The token is the LAST PATH SEGMENT and never `?token=`, which is what `buildLink` appends.
   */
  publicSignup: '/signup',
  signupVerify: '/signup/verify',
  /*
   * ── 0041 · WHERE THE CLEARING PROVIDER SENDS THE BROWSER BACK. ──────────────────────────
   *
   * ONE ADDRESS FOR SUCCESS AND FOR FAILURE, which is Cardcom's own advice applied: their
   * documentation says not to rely on the success page and to ask their servers what happened.
   * A product with a `/payment/success` route would have a screen saying "paid" that can be
   * reached by typing it.
   *
   * ONE ADDRESS FOR BOTH JOURNEYS, too. A registration and a card replacement come back to the
   * same screen because the question is the same question, and the reference in the last segment
   * is what says which page is being settled.
   *
   * THE REFERENCE IS A PATH SEGMENT AND NOT `?ref=`, for the reason every mailed address in this
   * file gives. It is not a one-time credential -- it authorises nothing and reveals nothing but
   * whether a card was accepted for a page its owner already knows about -- but a query string is
   * the thing that gets copied into analytics tags wholesale, and there is no reason to put it
   * there.
   */
  paymentReturn: '/payment/return',

  /**
   * THE CUSTOMER WORKSPACE HOME — the approved customer kit’s first rail entry, הבית שלי.
   *
   * It is a DIFFERENT place from the company list. The kit names both and the product had only the
   * second, so arriving at the office put a person straight into a picker rather than into their
   * own work. This is the landing now; `companies` keeps its own address and its own screen.
   */
  tenantHome: '/home',
  /** Tenant Workspace landing: the user's companies (09 §5.1). */
  companies: '/companies',

  /** `/c/{company_uid}` — company home. */
  companyHome: (companyUid: string): string => `/c/${uid('companyUid', companyUid)}`,

  /** `/c/{company_uid}/m/{module}` — module home. */
  moduleHome: (companyUid: string, moduleCode: string): string =>
    `/c/${uid('companyUid', companyUid)}/m/${encodeURIComponent(moduleCode)}`,

  /** `/c/{company_uid}/m/{module}/{parent}` — parent screen. */
  parentScreen: (companyUid: string, moduleCode: string, parent: string): string =>
    `${routes.moduleHome(companyUid, moduleCode)}/${encodeURIComponent(parent)}`,

  /** 09 §5.7b — נייר השלמות is a first-class screen with its own address, not an error toast. */
  blockedDocument: (companyUid: string, documentUid: string): string =>
    `/c/${uid('companyUid', companyUid)}/documents/${uid('documentUid', documentUid)}/blocked`,

  /** The Platform Console. A separate surface, entered by an explicit action (09 §"two surfaces"). */
  platformConsole: '/platform',

  // ── Control Plane (PLATFORM surface) ────────────────────────────────────────────────────────
  //
  // Every address here begins `/platform/`, so the surface is legible in the address bar as well as
  // in the chrome. 04 I6: the two surfaces are never simultaneous, and a bookmarked platform
  // address opened in a tenant session is answered with a written state, never a silent switch.

  /** The customers the control plane administers. A parent screen, not a picker. */
  platformTenants: '/platform/tenants',

  /** The four provisioning steps: tenant → subscription → SKUs → first administrator. */
  platformTenantProvisioning: '/platform/tenants/new',

  /**
   * `/platform/tenants/{tenant_uid}/provisioning` — FINISHING a customer that already exists.
   *
   * ── WHY THIS ADDRESS HAD TO EXIST ────────────────────────────────────────────────
   *
   * Provisioning is four writes against four routes, and until now the only way to reach writes
   * 2-4 was to have just completed write 1 IN THE SAME PAGE: the wizard carried the tenant uid in
   * component state and nothing else could put it there. A customer whose creation stopped after
   * step 1 was therefore UNREACHABLE — `/platform/tenants/new` starts a new one, and re-entering
   * the same handle answers `slug_taken`, correctly.
   *
   * That is not hypothetical. On 2026-10-18 `rafi-test-20261018` was created on Staging and its
   * role copy failed with SQL 515; the tenant row committed, the roles did not, and the product
   * had no supported way back to it. `PlatformSubscriptionScreen` already describes this exact
   * state — *"ללקוח אין עדיין מנוי"* — and its next step pointed at `/new`, which is the one
   * address that cannot help. It points here instead.
   *
   * OA-0001 §3.9 is the reason this is a product address and not a runbook: *"Do not solve
   * initial provisioning through an undocumented SQL insert."* Repairing a half-provisioned
   * customer by hand in the database is the same forbidden act, one step later.
   */
  platformTenantProvisioningResume: (tenantUid: string): string =>
    `/platform/tenants/${uid('tenantUid', tenantUid)}/provisioning`,

  /** `/platform/tenants/{tenant_uid}` — one customer. */
  platformTenant: (tenantUid: string): string => `/platform/tenants/${uid('tenantUid', tenantUid)}`,

  /** `/platform/tenants/{tenant_uid}/subscription` — purchased capacity, plainly. */
  platformTenantSubscription: (tenantUid: string): string =>
    `/platform/tenants/${uid('tenantUid', tenantUid)}/subscription`,

  /**
   * `/platform/tenants/{tenant_uid}/commercial` — money, and only money.
   *
   * A separate address from the subscription screen because 08 §8.4 / CF-66 keep money OUT of
   * entitlement: two tenants identical but for a discount must produce byte-identical access
   * decisions, and *"if changing the price invalidated the entitlement cache, price would be part of
   * entitlement"*. A capacity screen carrying a discount form is the first place those two get welded
   * back together, so the discount routes were given their own place instead.
   */
  platformTenantCommercial: (tenantUid: string): string =>
    `/platform/tenants/${uid('tenantUid', tenantUid)}/commercial`,

  /**
   * Support grants — the ONLY bridge between the platform and a customer's data (04 §13.1.5, §6.4).
   *
   * A place of its own rather than a panel on a customer card, because a grant has a lifecycle that
   * outlives the screen it was requested from: it is requested by one principal, approved by a
   * DIFFERENT one, and revoked from either side. A queue that only existed inside one customer's
   * card would leave the approver — who is looking for "what is waiting on me", not for a customer —
   * with nowhere to stand.
   */
  platformSupportGrants: '/platform/support-grants',

  /**
   * The commercial home of the control plane — the whole book at once.
   *
   * A place of its own rather than a panel on the customer list, because the two answer different
   * questions: the list is "who are our customers", this is "what commercial state is the business
   * in". It carries NO amount, for the reason `platformTenantCommercial` states.
   */
  /**
   * THE PRODUCT CATALOGUE — plans, SKUs, and what each grants. X3 (R-2).
   *
   * Master §98 (M:725) lists *"Plans / SKUs / Module Pricing"* among the REQUIRED Platform navigation
   * items and §99 (M:728) requires the Platform Owner to be able to operate the commercial model with
   * zero customers. Until X3 this address did not exist, so the one person the Master makes
   * responsible for the catalogue could only fill it with a hand-typed INSERT.
   *
   * ONE address and not two: plans and SKUs are one catalogue and are read together — a plan is empty
   * until a SKU is included in it, and a SKU grants nothing until a module is attached. Splitting them
   * would make the dependency invisible on both halves.
   */
  platformCatalogue: '/platform/catalogue',
  /** 08 §11 #8 — the dated price catalogue every customer is sold from. */
  platformPriceBooks: '/platform/price-books',
  /** `/platform/price-books/{price_book_uid}` — one book's dated rows. 09 §1.3: a noun is a screen. */
  platformPriceBook: (priceBookUid: string): string =>
    `/platform/price-books/${uid('priceBookUid', priceBookUid)}`,
  platformBilling: '/platform/billing',
  /** 08 D4 — the commercial defaults the Owner changes without a deploy. Migration 0016. */
  platformCommercialPolicy: '/platform/commercial-policy',
  /* §10ב. The public door and the plans behind it. Reached from the catalogue, whose
     capability it shares: `platform.catalogue.plan.manage`, platform_owner only. */
  platformSignupDoor: '/platform/signup-door',
  /** Refusals, recorded runs, and what this build cannot measure. The tile that led nowhere. */
  platformHealth: '/platform/health',

  /**
   * The security home — how many ways into customer data are open right now.
   *
   * Above the grant queue rather than inside it: the queue is somebody's working list, and this is
   * the standing fact an Owner checks. It also states, in words, that no platform-wide audit trail
   * exists, because an empty audit screen and an absent one look identical and mean the opposite.
   */
  platformCharges: '/platform/charges',
  /** 2026-11-02. Who is due, what each would be charged, and the press that charges them. */
  platformBillingRun: '/platform/billing/run',
  /** 2026-11-03. Who has not paid, which stage they are in, and the day counts. */
  platformDunning: '/platform/dunning',
  platformSupportQueue: '/platform/support-queue',
  /** 2026-11-03. What customers asked for that they cannot do themselves. */
  platformChangeRequests: '/platform/change-requests',
  platformSecurity: '/platform/security',

  // ── Tenant administration (TENANT surface) ──────────────────────────────────────────────────
  //
  // No tenant uid rides in these addresses. 04 I6: the active tenant is a property of the SESSION,
  // so putting it in the URL would create a second source for it — and a URL that names a tenant is
  // a URL somebody will edit.

  /**
   * THE SETTINGS LANDING. A grid of category cards and nothing else on it — 2026-10-28.
   *
   * It held the organisation's own details until the Owner's reference made the shape plain: the
   * landing is a menu, and each category is a screen. The details moved to `tenantOrganisation`
   * below, which is the card called החברה שלי in his list.
   */
  tenantAdmin: '/tenant',
  /** פרטי הארגון — the organisation's own record, one level under the settings landing. */
  tenantOrganisation: '/tenant/organisation',
  tenantUsers: '/tenant/users',
  tenantWorkItems: '/tenant/work-items',
  /* 0030/0031: the office's reporting deadlines. A SETTING -- it configures what the
     scheduler raises -- so it hangs under settings, not beside the work list. */
  tenantReminderRules: '/tenant/reminder-rules',
  /*
   * ── התאמה אישית. **Owner, 2026-11-01.** *"העבר את ההתאמה האישית מדף הבית אל ההגדרות."* ───────
   *
   * UNDER SETTINGS AND NOT UNDER THE HOME SCREEN, although what it configures is the home screen.
   * `/home/personalisation` would have made it a child of a screen that is the top of the tree
   * and has no children; the settings landing is a grid of categories and this is one of them.
   *
   * It is also the one category that configures THE PERSON and not the office -- which is a
   * reason to name it plainly here rather than to invent a second landing for personal settings
   * with one card on it.
   */
  tenantPersonalisation: '/tenant/personalisation',
  /** A child screen and NOT a modal: an invitation is a workflow with a written consequence. */
  tenantUserInvitation: '/tenant/users/invite',
  /*
   * ONE PERSON'S CARD. **Section 6, 2026-10-27.**
   *
   * It sits UNDER the users list and beside `/tenant/users/invite`, which is a STATIC segment and
   * therefore ranks above this dynamic one in the router -- the wizard keeps its address.
   */
  tenantUser: (membershipUid: string): string =>
    `/tenant/users/${uid('membershipUid', membershipUid)}`,
  tenantCompanies: '/tenant/companies',
  tenantCompanyOnboarding: '/tenant/companies/new',
  /**
   * `/tenant/companies/{company_uid}` — the client's MASTER RECORD inside the office.
   *
   * Deliberately not `/c/{company_uid}`, which is the WORK surface: modules, periods, documents. This
   * is the administrative record — legal name, lifecycle state, external identifiers, source bindings,
   * portfolio — and it is reached from חברות בארגון under `companies.company.manage`. Two addresses for
   * two different questions about the same company, which is 09 §1.3 (a noun is a screen) applied to a
   * company that is both a thing you work in and a thing the office administers.
   */
  tenantCompany: (companyUid: string): string =>
    `/tenant/companies/${uid('companyUid', companyUid)}`,
  /** Roles — what a user may DO. Never which companies they may do it on (OA-0001 §3.7). */
  tenantRoles: '/tenant/roles',
  /**
   * DEFINING a role, which is not the same act as granting one.
   *
   * Its own address for the reason `tenantUserInvitation` has one: a workflow with a written
   * consequence is a child screen, not a panel. And specifically here — the assignment screen must
   * offer NO capability control (OA-0001 §3.7: the two axes never collapse into an admin flag), while
   * authoring is nothing BUT a capability list. Putting both on one screen would put a control that
   * grants every capability at once one scroll away from the picker that grants a role.
   *
   * No role code rides in the address. 09 §1.5: a URL carries only opaque uids, and a role code is a
   * business value — so the role being edited is chosen ON the screen, exactly as the person being
   * granted a role is chosen on the assignment screen.
   */
  tenantRoleAuthoring: '/tenant/roles/authoring',
  /** Company access — which companies they may do it on. The other half, deliberately apart. */
  tenantCompanyAccess: '/tenant/access',
  tenantSubscription: '/tenant/subscription',
  /**
   * §11/§12 — the office's own money: the documents it was charged, and cancelling.
   *
   * ITS OWN ADDRESS AND NOT A PANEL ON `/tenant/subscription`, because that screen's stated
   * boundary is that money is not on it (08 §8.4). Two tabs, and the selected one is in the
   * fragment so support can send somebody to the right half.
   */
  tenantBilling: '/tenant/billing',
  /** Source connections and their state. NOT CONFIGURED is not a kind of ERROR (07 N-5). */
  tenantSources: '/tenant/sources',
  /**
   * `/tenant/sources/{connection_uid}` — one connection: configure it, test it, map its companies.
   *
   * A child screen and NOT a modal. 09 §2.1: configuration is six fields, the external-company queue
   * is a table the user reads row by row (M4), and mapping opens a decision per row (M6). Three of the
   * six modal tests fail, so it is a screen with an address.
   */
  tenantSourceConnection: (connectionUid: string): string =>
    `/tenant/sources/${uid('connectionUid', connectionUid)}`,
  /** The office's own audit and security surface (04 §8.2 — its OWN events, never another's). */
  tenantSecurity: '/tenant/security',

  // ── Security (either surface — identity belongs to the account, not to a tenant) ────────────
  /**
   * The ACCOUNT's own security: where it is signed in, and its second factor.
   *
   * Reachable from both surfaces, because identity belongs to the account and not to a tenant — a
   * platform principal has no tenant at all and still has sessions and an MFA obligation. It is the
   * parent of the two enrolment/challenge addresses that already existed.
   */
  /*
   * Section 1, 2026-10-29. BESIDE `/security` AND NOT INSIDE IT: the two answer different
   * questions. `/security` is the account's credentials -- password, second factor, sessions --
   * and this is who the person is. Putting details under security would say that editing a phone
   * number is a security act, and putting security under details would bury the screen somebody
   * reaches for when they think an account is compromised.
   */
  personalDetails: '/me',
  personalSecurity: '/security',
  mfaEnrolment: '/security/mfa/enrolment',
  mfaChallenge: '/security/mfa/challenge',
} as const;

/**
 * 09 §1.5: the period rides as a query parameter and is itself a uid.
 * Preserved across a company switch where it exists for the new company (09 §5.1).
 */
export function withPeriod(path: string, periodUid: string | null): string {
  if (periodUid === null) return path;
  return `${path}?period=${encodeURIComponent(uid('periodUid', periodUid))}`;
}

/** Route path patterns handed to React Router. Kept beside the builders so they cannot drift. */
export const routePatterns = {
  signIn: '/signin',
  welcome: '/welcome',
  /*
   * THE TOKEN IS A ROUTE PARAMETER, because it is a PATH SEGMENT on the wire. **X8.**
   *
   * `@glpoint/mail`'s `buildLink` appends `/<token>` to the address above, so a pattern without
   * `/:token` matches nothing the product mails — which is exactly what it did: the mail side said
   * `/invitations/accept/<token>`, this side served `/invitation`, and no test compared them because
   * the mail side had no production caller until `FileMailSink`.
   *
   * The parameter is NOT validated by `uid()`. A one-time token is not an entity uid — it has no
   * `xxx_` prefix and no business meaning — and the only thing permitted to judge it is the server
   * that holds its SHA-256. A client-side shape check here would refuse valid tokens on a format
   * change and would still refuse nothing an attacker sends.
   */
  invitationAccept: '/invitation/:token',
  passwordReset: '/signin/reset/:token',
  emailChangeConfirm: '/verify-email/:token',
  publicSignup: '/signup',
  signupVerify: '/signup/verify/:token',
  /* 0041. The provider returns to this address; the last segment is OUR reference for the page. */
  paymentReturn: '/payment/return/:reference',
  tenantHome: '/home',
  companies: '/companies',
  companyHome: '/c/:companyUid',
  moduleHome: '/c/:companyUid/m/:moduleCode',
  parentScreen: '/c/:companyUid/m/:moduleCode/:parent',
  blockedDocument: '/c/:companyUid/documents/:documentUid/blocked',
  platformConsole: '/platform',

  // A static segment out-ranks a parameter in React Router's matcher, so `/platform/tenants/new`
  // reaches the provisioning screen and never the detail screen with `tenantUid === 'new'` — which
  // `uid()` would refuse anyway, loudly, rather than putting a business word in an address.
  platformTenants: '/platform/tenants',
  platformTenantProvisioning: '/platform/tenants/new',
  platformTenant: '/platform/tenants/:tenantUid',
  // A longer static tail out-ranks the bare parameter, so this never reaches the detail screen.
  platformTenantProvisioningResume: '/platform/tenants/:tenantUid/provisioning',
  platformTenantSubscription: '/platform/tenants/:tenantUid/subscription',
  platformTenantCommercial: '/platform/tenants/:tenantUid/commercial',
  platformSupportGrants: '/platform/support-grants',
  /** X3 (R-2) — the product catalogue's own address. */
  platformCatalogue: '/platform/catalogue',
  /** 08 §11 #8 — the dated price catalogue every customer is sold from. */
  platformPriceBooks: '/platform/price-books',
  platformPriceBook: '/platform/price-books/:priceBookUid',
  platformBilling: '/platform/billing',
  platformCommercialPolicy: '/platform/commercial-policy',
  /* §10ב. The public door and the plans behind it. Reached from the catalogue, whose
     capability it shares: `platform.catalogue.plan.manage`, platform_owner only. */
  platformSignupDoor: '/platform/signup-door',
  platformHealth: '/platform/health',
  platformCharges: '/platform/charges',
  /** 2026-11-02. Who is due, what each would be charged, and the press that charges them. */
  platformBillingRun: '/platform/billing/run',
  /** 2026-11-03. Who has not paid, which stage they are in, and the day counts. */
  platformDunning: '/platform/dunning',
  platformSupportQueue: '/platform/support-queue',
  /** 2026-11-03. What customers asked for that they cannot do themselves. */
  platformChangeRequests: '/platform/change-requests',
  platformSecurity: '/platform/security',

  tenantAdmin: '/tenant',
  tenantOrganisation: '/tenant/organisation',
  tenantUsers: '/tenant/users',
  tenantWorkItems: '/tenant/work-items',
  /* 0030/0031: the office's reporting deadlines. A SETTING -- it configures what the
     scheduler raises -- so it hangs under settings, not beside the work list. */
  tenantReminderRules: '/tenant/reminder-rules',
  tenantPersonalisation: '/tenant/personalisation',
  tenantUserInvitation: '/tenant/users/invite',
  tenantUser: '/tenant/users/:membershipUid',
  tenantCompanies: '/tenant/companies',
  tenantCompanyOnboarding: '/tenant/companies/new',
  tenantCompany: '/tenant/companies/:companyUid',
  tenantRoles: '/tenant/roles',
  tenantRoleAuthoring: '/tenant/roles/authoring',
  tenantCompanyAccess: '/tenant/access',
  tenantSubscription: '/tenant/subscription',
  tenantBilling: '/tenant/billing',
  tenantSources: '/tenant/sources',
  tenantSourceConnection: '/tenant/sources/:connectionUid',
  tenantSecurity: '/tenant/security',

  /*
   * Section 1, 2026-10-29. BESIDE `/security` AND NOT INSIDE IT: the two answer different
   * questions. `/security` is the account's credentials -- password, second factor, sessions --
   * and this is who the person is. Putting details under security would say that editing a phone
   * number is a security act, and putting security under details would bury the screen somebody
   * reaches for when they think an account is compromised.
   */
  personalDetails: '/me',
  personalSecurity: '/security',
  mfaEnrolment: '/security/mfa/enrolment',
  mfaChallenge: '/security/mfa/challenge',
} as const;

/*
 * THE ADDRESSES THAT ARE REACHED WITHOUT A SESSION, AND THE SET IS A PRODUCT FACT.
 *
 * -- WHY THIS LIST HAS TO EXIST ------------------------------------------------------------------
 *
 * `App.tsx` reads `auth.whoami.read` on every address change and registers one handler for a 401:
 * go to sign-in. Both are right for the product's interior. On an address that is reached BEFORE a
 * session exists they are wrong together, and the result is an eviction rather than an error --
 * a correct 200 from the sign-in route, a correct 401 from `/api/me`, and a browser back on
 * `/signin` with nothing to display. Measured on the live estate 2026-10-15; reproduced in
 * `screens/signInTransition.dom.test.tsx`.
 *
 * -- WHY IT IS DECLARED HERE AND NOT DERIVED -----------------------------------------------------
 *
 * The server already states this boundary: these five are served by `publicRoute` while the rest of
 * the product is `userRoute`. The client could infer it per request from the operation registry,
 * and that was considered and rejected -- it would make every screen's 401 depend on route metadata
 * the screen never mentions, and the defect would return the first time a route's scope changed
 * without anyone thinking about navigation. Five addresses, named, reviewable at a glance, in the
 * file that is already the only place a URL is constructed (09 1.5).
 *
 * TWO OF THEM ARE THE MAIL LINKS, and they are why this is not an MFA-specific fix: `@glpoint/mail`
 * builds `invitationAccept` and `passwordReset` into every invitation and every reset message. They
 * are pre-session for the same unavoidable reason the MFA doors are, so the same eviction applied to
 * them -- unobserved only because outbound mail is deliberately unconfigured on Staging and nobody
 * has followed one yet.
 *
 * `personalSecurity` is NOT here and must not be: `/security` is the signed-in account's own screen.
 * It is a PREFIX of the two MFA addresses, which is why membership is decided by an EXACT pattern
 * match and never by `startsWith`.
 */
export const preSessionRoutePatterns: readonly string[] = [
  routePatterns.signIn,
  /* The public page. Reached BEFORE anybody has an account, which is the whole point of it. */
  routePatterns.welcome,
  routePatterns.invitationAccept,
  routePatterns.emailChangeConfirm,
  routePatterns.passwordReset,
  routePatterns.mfaEnrolment,
  routePatterns.mfaChallenge,
];
